import { zodResolver } from '@hookform/resolvers/zod';
import { type AuthResponse, type LoginInput, loginInput } from '@hudhuria/shared';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { DeskIcon } from '~/components/icons';
import { FieldError, PrimaryButton, RingLogo, SecondaryButton, TextField } from '~/components/ui';
import { ApiError, api, ok } from '~/lib/api';
import { completeSignIn } from '~/lib/auth';
import { useSession } from '~/session/session';

export const Route = createFileRoute('/sign-in')({ component: SignIn });

function SignIn() {
  const navigate = useNavigate();
  const { desk } = useSession();
  const form = useForm<LoginInput>({
    resolver: zodResolver(loginInput),
    defaultValues: { email: '', password: '' },
  });
  const login = useMutation({
    mutationFn: (v: LoginInput) => ok<AuthResponse>(api.auth.login.$post({ json: v })),
    onSuccess: (res) => void navigate({ to: completeSignIn(res), replace: true }),
    onError: (e) => {
      if (e instanceof ApiError && e.status === 401) form.setError('password', { message: e.message });
      else form.setError('root', { message: e.message });
    },
  });
  const { errors } = form.formState;

  return (
    <form
      onSubmit={form.handleSubmit((v) => login.mutate(v))}
      className="flex flex-1 animate-fade-in flex-col px-[26px] pt-safe-10 pb-safe scroll-y"
      noValidate
    >
      <RingLogo size={54} />
      <div className="mt-[26px] font-display text-[30px] font-bold tracking-[-0.02em]">Welcome back</div>
      <div className="mt-2 text-[15px] text-slate-500">Sign in to your institution's account.</div>
      <div className="mt-[30px] flex flex-col gap-3">
        <TextField
          label="Work email"
          type="email"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="none"
          placeholder="you@institution.org"
          error={errors.email?.message}
          {...form.register('email')}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="current-password"
          placeholder="••••••••"
          error={errors.password?.message}
          {...form.register('password')}
        />
        <Link to="/forgot-password" className="self-end text-[13px] font-semibold text-brand no-underline">
          Forgot password?
        </Link>
        <FieldError>{errors.root?.message}</FieldError>
      </div>
      <div className="mt-auto flex flex-col gap-3 pt-6">
        <PrimaryButton type="submit" loading={login.isPending}>
          Sign in
        </PrimaryButton>
        {desk && (
          <SecondaryButton onClick={() => void navigate({ to: '/desk' })}>
            <DeskIcon className="text-brand" />
            Pick your name instead
          </SecondaryButton>
        )}
      </div>
    </form>
  );
}
